import React from "react";
import { EmptyState } from "components/states";
import { formatNumber } from "utils/format";
import { getQuestionTypeMeta, isChoiceQuestion, isNumericQuestion } from "./questionTypes";

const MAX_OPTION_LENGTH = 15;

const getSummaryValue = ({ type, stats }) => {
  if (!stats || stats.count === 0) {
    return <span className="text-fg-muted">No responses</span>;
  }

  if (isNumericQuestion(type)) {
    return <span className="font-medium text-fg">Avg: {stats.mean}</span>;
  }

  if (isChoiceQuestion(type)) {
    const entries = Object.entries(stats.distribution || {});
    if (entries.length === 0) {
      return <span className="text-fg-muted">{formatNumber(stats.count)} responses</span>;
    }
    entries.sort((a, b) => b[1] - a[1]);
    const [topOption, topCount] = entries[0];
    const percent = stats.count > 0 ? Math.round((topCount / stats.count) * 100) : 0;
    const displayOption =
      topOption.length > MAX_OPTION_LENGTH
        ? `${topOption.substring(0, MAX_OPTION_LENGTH)}…`
        : topOption;
    return (
      <span className="font-medium text-fg">
        {displayOption} ({percent}%)
      </span>
    );
  }

  return <span className="text-fg-muted">{formatNumber(stats.count)} responses</span>;
};

const QuestionsList = ({ questions = [], selectedId, onSelect }) => {
  if (!questions || questions.length === 0) {
    return <EmptyState variant="inline" title="No questions found" />;
  }

  return (
    <ul className="divide-y divide-line">
      {questions.map((question) => {
        const isSelected = selectedId === question.questionId;
        const TypeIcon = getQuestionTypeMeta(question.type).icon;

        return (
          <li key={question.questionId}>
            <button
              type="button"
              aria-pressed={isSelected}
              onClick={() => onSelect(question.questionId)}
              className={`focus-ring flex w-full items-start gap-3 px-4 py-3 text-left transition-colors ${
                isSelected ? "bg-surface-subtle" : "hover:bg-surface-subtle/60"
              }`}
            >
              <TypeIcon
                aria-hidden="true"
                className={`mt-0.5 h-4 w-4 shrink-0 ${isSelected ? "text-primary dark:text-accent" : "text-fg-muted"}`}
              />
              <span className="min-w-0 flex-1">
                <span
                  className={`line-clamp-2 text-sm ${
                    isSelected ? "font-semibold text-fg" : "text-fg"
                  }`}
                >
                  {question.questionText}
                </span>
                <span className="mt-1 flex items-center justify-between gap-2 text-xs">
                  <span className="text-fg-muted">
                    {formatNumber(question.stats?.count || 0)} responses
                  </span>
                  {getSummaryValue(question)}
                </span>
              </span>
            </button>
          </li>
        );
      })}
    </ul>
  );
};

export default QuestionsList;
